// k-currency.directive.ts
import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
  selector: 'input[matInput][kCurrency]',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: KCurrencyDirective, // no forwardRef
      multi: true,
    },
  ],
})
export class KCurrencyDirective implements ControlValueAccessor, OnInit, OnChanges {
  /** If true, hide the suffix while the input is focused. */
  @Input() hideSuffixOnFocus = true;

  /** Fraction digits to show in k-units */
  @Input() fractionDigits = 1;

  /** Suffix text */
  @Input() suffix = ' k$';

  /** Optional locale for number formatting */
  @Input() locale = 'en-US';
  @Input() kAmountEnabled = true;

  private _onChange: (value: number | null) => void = () => {};
  private _onTouched: () => void = () => {};
  private _rawValue: number | null = null; // stored in full units (dollars)

  constructor(private el: ElementRef<HTMLInputElement>) {}

  // Ensure initial rendering even if input is disabled at template-load time
  ngOnInit(): void {
    // Force text type so suffix displays
    
    // if (!this.kAmountEnabled) {
    //   return; // directive disabled -> do nothing
    // }

    if (this.el.nativeElement.type !== 'text') {
      this.el.nativeElement.type = 'text';
    }

    // If the DOM already has a value (SSR, prefilled, or static HTML), normalize it
    if (this._rawValue == null && this.el.nativeElement.value) {
      const kValue = this.parseToNumber(this.el.nativeElement.value);
      if (kValue != null && this.kAmountEnabled) {
        this._rawValue = Math.round(kValue * 1000);
      }
    }

    // Render once on init to cover "disabled at start" scenarios
    this.renderFromModel();
  }

  // Re-render if @Input()s change (suffix/locale/fractionDigits)
  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['suffix'] ||
      changes['kAmountEnabled'] ||
      changes['locale'] ||
      changes['fractionDigits']
    ) {
      this.renderFromModel();
    }
  }

  // ---- ControlValueAccessor API ----
  writeValue(value: number | null): void {
    this._rawValue = value;
    this.renderFromModel();
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.el.nativeElement.disabled = isDisabled;
    // Always re-render on disabled toggle so the UI shows the suffix/value
    this.renderFromModel();
  }

  // ---- Events from the input ----
  // Note: These won't fire when disabled, but that's fine — we render elsewhere for disabled state.

  @HostListener('focus')
  onFocus() {
    if (this.hideSuffixOnFocus && !this.el.nativeElement.disabled) {
      if (this.kAmountEnabled) {
        const k = this._rawValue != null ? this._rawValue / 1000 : null;
        this.el.nativeElement.value =
          k == null ? '' : this.formatNumber(k, false /* no suffix */);
      } else {
        this.el.nativeElement.value = this._rawValue != null ? this._rawValue.toString() : '';
      }
      requestAnimationFrame(() =>
        this.el.nativeElement.setSelectionRange(
          this.el.nativeElement.value.length,
          this.el.nativeElement.value.length
        )
      );
    }
  }

  @HostListener('blur')
  onBlur() {
    if (!this.el.nativeElement.disabled) {
      this._onTouched();
      this.renderFromModel();
    }
  }

  @HostListener('input', ['$event'])
  onInput(e: Event) {
    if (this.el.nativeElement.disabled) return;
    const target = e.target as HTMLInputElement;
    const kValue = this.parseToNumber(target.value);
    if (kValue == null) {
      this._rawValue = null;
      this._onChange(null);
      return;
    }
    
    const full = this.kAmountEnabled ? Math.round(kValue * 1000) : kValue;
    this._rawValue = full;
    this._onChange(full);
  }

  // ---- Helpers ----

  private renderFromModel() {
    if(!this.kAmountEnabled && this._rawValue !== undefined && this._rawValue !== null){
      const nfmt = new Intl.NumberFormat(this.locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: Math.max(0, Math.min(6, this.fractionDigits)),
      });
      this.el.nativeElement.value = nfmt.format(this._rawValue);
      return;
    }
      
    const k = this._rawValue != null ? this._rawValue / 1000 : null;
    this.el.nativeElement.value =
      k == null ? '' : this.formatNumber(k, false /* with suffix */);
  }

  private formatNumber(valueInK: number, withSuffix: boolean): string {
    const min = 0;
    const max = Math.max(0, Math.min(6, this.fractionDigits));
    const nfmt = new Intl.NumberFormat(this.locale, {
      minimumFractionDigits: min,
      maximumFractionDigits: max,
    });
    return nfmt.format(valueInK) + (withSuffix ? this.suffix : '');
  }

  private parseToNumber(input: string): number | null {
    if (!input) return null;

    // Remove suffix first (in case user pasted the shown text)
    let cleaned = input.replace(this.suffix, '').trim();

    // Remove all but digits, separators, and minus
    cleaned = cleaned.replace(/[^\d.,-]/g, '');

    if (!cleaned) return null;

    // Determine the actual grouping/decimal characters for this locale
    // (formatNumber() renders with grouping on, e.g. "1,000" for en-US,
    // so we must strip that exact grouping char rather than guessing).
    const { group, decimal } = this.getLocaleSeparators();

    if (group) {
      cleaned = cleaned.split(group).join('');
    }
    if (decimal !== '.') {
      cleaned = cleaned.replace(decimal, '.');
    }

    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private getLocaleSeparators(): { group: string; decimal: string } {
    const parts = new Intl.NumberFormat(this.locale).formatToParts(1234.5);
    const group = parts.find(p => p.type === 'group')?.value ?? ',';
    const decimal = parts.find(p => p.type === 'decimal')?.value ?? '.';
    return { group, decimal };
  }
}
``